import { message } from 'antd';
import type { TFunction } from 'i18next';

/** How long to keep the blob URL alive before revoking. 60 s is long enough
 *  for any browser viewer to have parsed and buffered the bytes, short
 *  enough that previewing many files doesn't stack up MB of live blob URLs.
 *  If a slow renderer edge case shows up we can bump this — the tab already
 *  has a copy of the parsed content by the time the URL is gone. */
const PREVIEW_URL_TTL_MS = 60_000;
/** Half a second is enough for the browser's download machinery to read the
 *  blob URL into its own stream. Revoking earlier can truncate the download
 *  on Safari. */
const DOWNLOAD_URL_TTL_MS = 500;

/**
 * Open a picked File in a new tab via a blob URL, then revoke after a TTL
 * so the URL doesn't leak into long-lived tabs. Falls back to a
 * `message.warning` when the popup was blocked so the advisor knows the
 * click did fire — a silent no-op looks like a broken button.
 *
 * Shared by `AdditionalDocumentsStep` (per-file Preview button) and
 * `PdfGenerationStep` (per-uploaded-file Preview button). Same behavior
 * either side so a click on the same file feels identical across the flow.
 *
 * `popupBlockedKey` is the i18n key to feed `message.warning` when the
 * popup was blocked. Callers pass their own so the copy matches the
 * step's other translated strings.
 */
export function openFileInNewTab(
  file: File,
  t: TFunction,
  popupBlockedKey = 'formRenderer.additionalDocuments.popupBlocked',
): void {
  const url = URL.createObjectURL(file);
  const win = window.open(url, '_blank', 'noopener,noreferrer');
  if (!win) {
    URL.revokeObjectURL(url);
    void message.warning(t(popupBlockedKey));
    return;
  }
  window.setTimeout(() => URL.revokeObjectURL(url), PREVIEW_URL_TTL_MS);
}

/**
 * Trigger a browser download of a picked File under its original filename.
 * Uses the anchor-with-download-attr pattern — the browser handles the
 * "Save As" dialog. Revokes the blob URL after a short delay so the download
 * completes but no long-lived URL leaks.
 */
export function downloadFileToDevice(file: File): void {
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), DOWNLOAD_URL_TTL_MS);
}
